import React, { useState, useEffect } from 'react'
import {
  Card,
  Descriptions,
  Tag,
  Button,
  Space,
  Typography,
  Modal,
  Input,
  message,
  Spin,
  Progress,
  Timeline
} from 'antd'
import {
  ArrowLeftOutlined,
  PlayCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  BookOutlined,
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined
} from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { getTaskDetail, startTask, submitTask, requestExtension, type WritingTask } from '../../api/textbook'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'

dayjs.extend(relativeTime)
dayjs.locale('zh-cn')

const { Title, Text, Paragraph } = Typography
const { TextArea } = Input

const TaskDetail: React.FC = () => {
  const navigate = useNavigate()
  const { taskId } = useParams<{ taskId: string }>()
  const [task, setTask] = useState<WritingTask | null>(null)
  const [loading, setLoading] = useState(false)
  const [extensionModalVisible, setExtensionModalVisible] = useState(false)
  const [extensionReason, setExtensionReason] = useState('')

  useEffect(() => {
    if (taskId) {
      loadTaskDetail()
    }
  }, [taskId])

  const loadTaskDetail = async () => {
    if (!taskId) return
    
    try {
      setLoading(true)
      const taskData = await getTaskDetail(Number(taskId))
      setTask(taskData)
    } catch (error: any) {
      console.error('加载任务详情失败:', error)
      message.error('加载任务详情失败: ' + (error.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }

  const handleStartTask = async () => {
    if (!task) return
    
    try {
      await startTask(task.taskId)
      message.success('任务已开始')
      loadTaskDetail()
    } catch (error: any) {
      message.error('开始任务失败: ' + (error.message || '未知错误'))
    }
  }

  const handleSubmitTask = async () => {
    if (!task) return
    
    Modal.confirm({
      title: '确认提交',
      content: '确定要提交此任务吗？提交后将无法修改。',
      onOk: async () => {
        try {
          await submitTask(task.taskId)
          message.success('任务已提交')
          loadTaskDetail()
        } catch (error: any) {
          message.error('提交任务失败: ' + (error.message || '未知错误'))
        }
      }
    })
  }

  const handleRequestExtension = () => {
    setExtensionReason('')
    setExtensionModalVisible(true)
  }

  const confirmExtension = async () => {
    if (!task) return
    if (!extensionReason.trim()) {
      message.warning('请输入延期原因')
      return
    }
    
    try {
      await requestExtension(task.taskId, extensionReason)
      message.success('延期申请已提交')
      setExtensionModalVisible(false)
      setExtensionReason('')
      loadTaskDetail()
    } catch (error: any) {
      message.error('申请延期失败: ' + (error.message || '未知错误'))
    }
  }

  const getStatusTag = (status: string, deadline?: string) => {
    const now = dayjs()
    const isOverdue = deadline && dayjs(deadline).isBefore(now) && status !== 'completed' && status !== 'cancelled'
    
    if (isOverdue) {
      return <Tag color="red" icon={<ExclamationCircleOutlined />}>已逾期</Tag>
    }
    
    switch (status) {
      case 'pending':
        return <Tag color="orange" icon={<ClockCircleOutlined />}>待开始</Tag>
      case 'in_progress':
        return <Tag color="blue" icon={<PlayCircleOutlined />}>进行中</Tag>
      case 'completed':
        return <Tag color="green" icon={<CheckCircleOutlined />}>已完成</Tag>
      case 'cancelled':
        return <Tag color="default">已取消</Tag>
      default:
        return <Tag>{status}</Tag>
    }
  }

  const getPriorityTag = (priority: string) => {
    const priorityMap: Record<string, { color: string; text: string }> = {
      urgent: { color: 'red', text: '紧急' },
      high: { color: 'orange', text: '高' },
      normal: { color: 'blue', text: '普通' },
      low: { color: 'default', text: '低' }
    }
    const config = priorityMap[priority] || { color: 'default', text: priority }
    return <Tag color={config.color}>{config.text}</Tag>
  }

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!task) {
    return (
      <div style={{ padding: '24px' }}>
        <Card>任务不存在</Card>
      </div>
    )
  }

  const isOverdue = task.deadline && dayjs(task.deadline).isBefore(dayjs()) && 
                   task.status !== 'completed' && task.status !== 'cancelled'

  return (
    <div style={{ padding: '24px' }}>
      <Space style={{ marginBottom: 16 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/textbook/tasks')}
        >
          返回列表
        </Button>
      </Space>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Title level={3} style={{ margin: 0 }}>
            <FileTextOutlined style={{ marginRight: 8 }} />
            {task.taskTitle}
          </Title>
          <Space>
            {task.status === 'pending' && (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={handleStartTask}
              >
                开始任务
              </Button>
            )}
            {task.status === 'in_progress' && (
              <>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleSubmitTask}
                >
                  提交任务
                </Button>
                {isOverdue && (
                  <Button
                    danger
                    onClick={handleRequestExtension}
                  >
                    申请延期
                  </Button>
                )}
              </>
            )}
          </Space>
        </Space>

        <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
          <Descriptions.Item label="任务状态">
            {getStatusTag(task.status, task.deadline)}
          </Descriptions.Item>
          <Descriptions.Item label="优先级">
            {getPriorityTag(task.priority)}
          </Descriptions.Item>
          <Descriptions.Item label="任务类型">
            {task.taskType === 'writing' ? '编写' : task.taskType === 'review' ? '审核' : '修订'}
          </Descriptions.Item>
          <Descriptions.Item label="进度">
            <Progress percent={task.progress} />
          </Descriptions.Item>
          <Descriptions.Item label="教材">
            <Space>
              <BookOutlined />
              {task.textbookTitle || `教材ID: ${task.textbookId}`}
            </Space>
          </Descriptions.Item>
          {task.chapterTitle && (
            <Descriptions.Item label="章节">
              {task.chapterNumber && `第${task.chapterNumber}章 `}
              {task.chapterTitle}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="分配者">
            <Space>
              <UserOutlined />
              {task.assignerName || `ID: ${task.assignerId}`}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="截止时间">
            <Space>
              <CalendarOutlined />
              {task.deadline ? (
                <span style={{ color: isOverdue ? '#ff4d4f' : undefined }}>
                  {dayjs(task.deadline).format('YYYY-MM-DD HH:mm')}
                  {isOverdue && ' (已逾期)'}
                </span>
              ) : (
                '未设置'
              )}
            </Space>
          </Descriptions.Item>
          {task.startTime && (
            <Descriptions.Item label="开始时间">
              {dayjs(task.startTime).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
          )}
          {task.completeTime && (
            <Descriptions.Item label="完成时间">
              {dayjs(task.completeTime).format('YYYY-MM-DD HH:mm')}
            </Descriptions.Item>
          )}
        </Descriptions>

        {task.taskDescription && (
          <Card title="任务描述" style={{ marginBottom: 24 }}>
            <Paragraph>{task.taskDescription}</Paragraph>
          </Card>
        )}

        {task.remark && (
          <Card title="备注信息" style={{ marginBottom: 24 }}>
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{task.remark}</Paragraph>
          </Card>
        )}

        {/* 时间线 */}
        <Card title="任务时间线">
          <Timeline>
            <Timeline.Item color="blue">
              <Text strong>任务创建</Text>
              <br />
              <Text type="secondary">
                {dayjs(task.createTime).format('YYYY-MM-DD HH:mm')} ({dayjs(task.createTime).fromNow()})
              </Text>
            </Timeline.Item>
            {task.startTime && (
              <Timeline.Item color="green">
                <Text strong>任务开始</Text>
                <br />
                <Text type="secondary">
                  {dayjs(task.startTime).format('YYYY-MM-DD HH:mm')} ({dayjs(task.startTime).fromNow()})
                </Text>
              </Timeline.Item>
            )}
            {task.deadline && (
              <Timeline.Item color={isOverdue ? 'red' : 'orange'}>
                <Text strong>截止时间</Text>
                <br />
                <Text type="secondary">
                  {dayjs(task.deadline).format('YYYY-MM-DD HH:mm')} ({dayjs(task.deadline).fromNow()})
                </Text>
              </Timeline.Item>
            )}
            {task.completeTime && (
              <Timeline.Item color="green">
                <Text strong>任务完成</Text>
                <br />
                <Text type="secondary">
                  {dayjs(task.completeTime).format('YYYY-MM-DD HH:mm')} ({dayjs(task.completeTime).fromNow()})
                </Text>
              </Timeline.Item>
            )}
          </Timeline>
        </Card>
      </Card>

      {/* 延期申请弹窗 */}
      <Modal
        title="申请延期"
        open={extensionModalVisible}
        onOk={confirmExtension}
        onCancel={() => {
          setExtensionModalVisible(false)
          setExtensionReason('')
        }}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>请输入延期原因：</Text>
        </div>
        <TextArea
          rows={4}
          value={extensionReason}
          onChange={(e) => setExtensionReason(e.target.value)}
          placeholder="请详细说明需要延期的原因..."
        />
      </Modal>
    </div>
  )
}

export default TaskDetail

